import { ScrapingField, AIServiceConfig } from "@/types/scraper";
import { SelectorError } from "./errors";
import {
  RemoteAIService,
  AIDetectionResult,
  defaultRemoteAIService,
} from "./remoteAI";

interface DetectionResult {
  selector: string;
  confidence: number;
  value?: string;
  element: Element;
  aiSuggested?: boolean;
}

interface ElementScore {
  element: Element;
  score: number;
  reason: string[];
}

export class ElementDetector {
  private static pricePatterns = [
    /^\$?\d+(\.\d{2})?$/, // $19.99 or 19.99
    /^(USD|EUR|GBP)\s*\d+(\.\d{2})?$/, // USD 19.99
    /^\d+(\.\d{2})?\s*(USD|EUR|GBP)$/, // 19.99 USD
    /^[£€]\d+(\.\d{2})?$/, // £19.99 or €19.99
    /^\d+[,.]\d{2}\s*[€£$]$/, // 19,99 € or 19.99 £
  ];

  private static skuPatterns = [
    /^SKU:\s*[\w-]+$/i,
    /^Item #:\s*[\w-]+$/i,
    /^Product Code:\s*[\w-]+$/i,
    /^[A-Z0-9]{6,12}$/, // Common SKU format
    /^\w{3,5}-\d{3,7}$/, // Format like ABC-12345
    /^\d{3,6}-\w{2,5}$/, // Format like 12345-ABC
  ];

  // Remote AI service integration
  private static remoteAIService = defaultRemoteAIService;
  private static aiConfig: AIServiceConfig = {
    provider: "local",
    enabled: false,
    confidenceThreshold: 0.7,
    preferAiSuggestions: true,
    modelName: "deepseek-coder-v1.5",
    useLocalServer: false,
    localServerUrl: "/api/deepseek/detect",
    localServerStatus: "unknown",
    requestTimeout: 10000,
    maxRetries: 2,
    detectionMode: "balanced",
    includePageContext: true,
    extractValues: true,
  };

  static async detectElements(
    field: ScrapingField,
  ): Promise<DetectionResult[]> {
    try {
      const results: DetectionResult[] = [];
      const startTime = performance.now();

      // Try Remote AI detection first if enabled
      if (this.aiConfig.enabled) {
        try {
          console.log(
            `Starting AI detection for field: ${field.id} using ${this.aiConfig.provider}`,
          );

          // Get the HTML content of the current page for the AI service
          const html = document.documentElement.outerHTML;
          const url = window.location.href;

          // Apply detection mode settings
          const detectionConfig = { ...this.aiConfig };
          if (detectionConfig.detectionMode === "aggressive") {
            detectionConfig.confidenceThreshold = Math.max(
              0.5,
              detectionConfig.confidenceThreshold - 0.1,
            );
          } else if (detectionConfig.detectionMode === "conservative") {
            detectionConfig.confidenceThreshold = Math.min(
              0.95,
              detectionConfig.confidenceThreshold + 0.1,
            );
          }

          // Call the remote AI service with the current configuration
          const aiResults = await this.remoteAIService.detectElements(
            field,
            html,
            url,
          );

          console.log(
            `AI detection returned ${aiResults.length} results for field: ${field.id}`,
          );

          if (aiResults.length > 0) {
            // Convert AI results to DetectionResult objects
            const detectionResults =
              await this.convertAIResultsToDetectionResults(aiResults);
            results.push(...detectionResults);

            // If AI results are confident enough, return them directly
            const highConfidenceResults = detectionResults.filter(
              (result) =>
                result.confidence >= detectionConfig.confidenceThreshold,
            );

            console.log(
              `Found ${highConfidenceResults.length} high confidence results (threshold: ${detectionConfig.confidenceThreshold})`,
            );

            if (
              highConfidenceResults.length > 0 &&
              this.aiConfig.preferAiSuggestions
            ) {
              const endTime = performance.now();
              console.log(
                `AI detection completed in ${Math.round(endTime - startTime)}ms`,
              );
              return highConfidenceResults;
            }
          }
        } catch (error) {
          console.error(
            `${this.aiConfig.provider} AI detection failed:`,
            error,
          );
          // Continue with traditional detection methods
        }
      }

      console.log(
        `Falling back to traditional detection methods for field: ${field.id}`,
      );

      // Fall back to traditional detection methods
      switch (field.id) {
        case "name":
          results.push(...(await this.detectProductName()));
          break;
        case "price":
          results.push(...(await this.detectPrice()));
          break;
        case "sku":
          results.push(...(await this.detectSKU()));
          break;
        case "description":
          results.push(...(await this.detectDescription()));
          break;
        case "image":
        case "imageUrls":
          results.push(...(await this.detectProductImage()));
          break;
        default:
          // Generic detection based on field id
          results.push(...(await this.detectGenericField(field.id)));
      }

      // Add data attributes to detected elements
      results.forEach((result) => {
        result.element.setAttribute("data-field-id", field.id);
        result.element.setAttribute(
          "data-confidence",
          result.confidence.toString(),
        );
        if (result.aiSuggested) {
          result.element.setAttribute("data-ai-suggested", "true");
        }
      });

      const endTime = performance.now();
      console.log(
        `Element detection completed in ${Math.round(endTime - startTime)}ms`,
      );
      return results;
    } catch (error) {
      throw new SelectorError(
        `Failed to detect elements for field ${field.id}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private static async detectProductName(): Promise<DetectionResult[]> {
    const candidates = document.querySelectorAll(
      'h1, [class*="title"], [class*="name"], [class*="product"], [itemprop="name"]',
    );
    const results: ElementScore[] = [];

    candidates.forEach((el) => {
      let score = 0;
      const reasons: string[] = [];

      // Check if it's a heading
      if (el.tagName === "H1") {
        score += 30;
        reasons.push("Main heading");
      } else if (el.tagName === "H2") {
        score += 20;
        reasons.push("Secondary heading");
      }

      // Check for schema.org markup
      if (el.getAttribute("itemprop") === "name") {
        score += 35;
        reasons.push("Schema.org name markup");
      }

      // Check classes and IDs
      const classAndId = (el.className + " " + el.id).toLowerCase();
      if (classAndId.includes("product") && classAndId.includes("name")) {
        score += 25;
        reasons.push("Product name in class/id");
      }
      if (classAndId.includes("title")) {
        score += 20;
        reasons.push("Title in class/id");
      }

      // Check content length
      const text = el.textContent?.trim() || "";
      if (text.length > 10 && text.length < 200) {
        score += 15;
        reasons.push("Appropriate text length");
      }

      // Check position
      if (el.getBoundingClientRect().top < window.innerHeight / 2) {
        score += 10;
        reasons.push("Located in upper half of page");
      }

      if (score > 30) {
        results.push({ element: el, score, reason: reasons });
      }
    });

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((result) => ({
        selector: this.generateSelector(result.element),
        confidence: Math.min(result.score / 100, 0.99),
        value: result.element.textContent?.trim(),
        element: result.element,
      }));
  }

  private static async detectPrice(): Promise<DetectionResult[]> {
    const candidates = document.querySelectorAll(
      '[class*="price"], [class*="cost"], [class*="amount"], [itemprop="price"]',
    );
    const results: ElementScore[] = [];

    candidates.forEach((el) => {
      let score = 0;
      const reasons: string[] = [];
      const text = el.textContent?.trim() || "";

      // Check for schema.org markup
      if (el.getAttribute("itemprop") === "price") {
        score += 40;
        reasons.push("Schema.org price markup");
      }

      // Check price patterns
      if (this.pricePatterns.some((pattern) => pattern.test(text))) {
        score += 40;
        reasons.push("Matches price format");
      }

      // Check classes and IDs
      const classAndId = (el.className + " " + el.id).toLowerCase();
      if (classAndId.includes("price")) {
        score += 25;
        reasons.push("Price in class/id");
      }
      if (classAndId.includes("current") || classAndId.includes("main")) {
        score += 15;
        reasons.push("Main/current price indicator");
      }

      // Check for currency symbols
      if (text.includes("$") || text.includes("€") || text.includes("£")) {
        score += 20;
        reasons.push("Contains currency symbol");
      }

      // Avoid sale prices or original prices if possible
      if (
        classAndId.includes("old") ||
        classAndId.includes("original") ||
        classAndId.includes("regular")
      ) {
        score -= 30;
        reasons.push("Likely an old/original price");
      }

      if (score > 30) {
        results.push({ element: el, score, reason: reasons });
      }
    });

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((result) => ({
        selector: this.generateSelector(result.element),
        confidence: Math.min(result.score / 100, 0.99),
        value: result.element.textContent?.trim(),
        element: result.element,
      }));
  }

  private static async detectSKU(): Promise<DetectionResult[]> {
    const candidates = document.querySelectorAll(
      '[class*="sku"], [class*="product-id"], [class*="item-number"], [itemprop="sku"]',
    );
    const results: ElementScore[] = [];

    candidates.forEach((el) => {
      let score = 0;
      const reasons: string[] = [];
      const text = el.textContent?.trim() || "";

      // Check for schema.org markup
      if (el.getAttribute("itemprop") === "sku") {
        score += 40;
        reasons.push("Schema.org SKU markup");
      }

      // Check SKU patterns
      if (this.skuPatterns.some((pattern) => pattern.test(text))) {
        score += 40;
        reasons.push("Matches SKU format");
      }

      // Check classes and IDs
      const classAndId = (el.className + " " + el.id).toLowerCase();
      if (classAndId.includes("sku")) {
        score += 25;
        reasons.push("SKU in class/id");
      }
      if (
        classAndId.includes("product-id") ||
        classAndId.includes("item-number")
      ) {
        score += 20;
        reasons.push("Product ID indicator");
      }

      // Check for common SKU labels
      const previousText = this.getPreviousTextNode(el)?.toLowerCase() || "";
      if (previousText.includes("sku") || previousText.includes("item #")) {
        score += 15;
        reasons.push("Has SKU label");
      }

      if (score > 30) {
        results.push({ element: el, score, reason: reasons });
      }
    });

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((result) => ({
        selector: this.generateSelector(result.element),
        confidence: Math.min(result.score / 100, 0.99),
        value: result.element.textContent?.trim(),
        element: result.element,
      }));
  }

  private static async detectDescription(): Promise<DetectionResult[]> {
    const candidates = document.querySelectorAll(
      '[class*="description"], [class*="desc"], [itemprop="description"], .product-description, #product-description, p:not(:empty)',
    );
    const results: ElementScore[] = [];

    candidates.forEach((el) => {
      let score = 0;
      const reasons: string[] = [];
      const text = el.textContent?.trim() || "";

      // Check for schema.org markup
      if (el.getAttribute("itemprop") === "description") {
        score += 40;
        reasons.push("Schema.org description markup");
      }

      // Check classes and IDs
      const classAndId = (el.className + " " + el.id).toLowerCase();
      if (classAndId.includes("description") || classAndId.includes("desc")) {
        score += 30;
        reasons.push("Description in class/id");
      }

      // Check content length - descriptions are usually longer
      if (text.length > 50) {
        score += 15;
        reasons.push("Appropriate text length for description");
      }

      // Paragraph tags are often used for descriptions
      if (el.tagName === "P" || el.tagName === "DIV") {
        score += 10;
        reasons.push("Common description element type");
      }

      if (score > 30) {
        results.push({ element: el, score, reason: reasons });
      }
    });

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((result) => ({
        selector: this.generateSelector(result.element),
        confidence: Math.min(result.score / 100, 0.99),
        value: result.element.textContent?.trim(),
        element: result.element,
      }));
  }

  private static async detectProductImage(): Promise<DetectionResult[]> {
    const candidates = document.querySelectorAll(
      'img[class*="product"], img[class*="main"], img[itemprop="image"], .product-image img, #product-image',
    );
    const results: ElementScore[] = [];

    candidates.forEach((el) => {
      let score = 0;
      const reasons: string[] = [];

      // Check for schema.org markup
      if (el.getAttribute("itemprop") === "image") {
        score += 40;
        reasons.push("Schema.org image markup");
      }

      // Check classes and IDs
      const classAndId = (el.className + " " + el.id).toLowerCase();
      if (classAndId.includes("product") && classAndId.includes("image")) {
        score += 30;
        reasons.push("Product image in class/id");
      }
      if (classAndId.includes("main") || classAndId.includes("primary")) {
        score += 20;
        reasons.push("Main/primary image indicator");
      }

      // Check image size - product images are usually larger
      const rect = el.getBoundingClientRect();
      if (rect.width > 200 && rect.height > 200) {
        score += 15;
        reasons.push("Large image size");
      }

      // Check if image has alt text containing product-related terms
      const alt = el.getAttribute("alt")?.toLowerCase() || "";
      if (alt.includes("product") || alt.includes("item")) {
        score += 10;
        reasons.push("Product-related alt text");
      }

      if (score > 30) {
        results.push({ element: el, score, reason: reasons });
      }
    });

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((result) => ({
        selector: this.generateSelector(result.element),
        confidence: Math.min(result.score / 100, 0.99),
        value: result.element.getAttribute("src"),
        element: result.element,
      }));
  }

  private static async detectGenericField(
    fieldId: string,
  ): Promise<DetectionResult[]> {
    // Try to find elements that might match the field ID
    const candidates = document.querySelectorAll(
      `[class*="${fieldId}"], [id*="${fieldId}"], [itemprop="${fieldId}"], [name="${fieldId}"]`,
    );
    const results: ElementScore[] = [];

    candidates.forEach((el) => {
      let score = 0;
      const reasons: string[] = [];

      // Check for schema.org markup
      if (el.getAttribute("itemprop") === fieldId) {
        score += 40;
        reasons.push(`Schema.org ${fieldId} markup`);
      }

      // Check classes and IDs
      const classAndId = (el.className + " " + el.id).toLowerCase();
      if (classAndId.includes(fieldId)) {
        score += 30;
        reasons.push(`${fieldId} in class/id`);
      }

      // Check element type
      if (el.tagName === "SPAN" || el.tagName === "DIV" || el.tagName === "P") {
        score += 10;
        reasons.push("Common data element type");
      }

      if (score > 20) {
        results.push({ element: el, score, reason: reasons });
      }
    });

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((result) => ({
        selector: this.generateSelector(result.element),
        confidence: Math.min(result.score / 100, 0.99),
        value: result.element.textContent?.trim(),
        element: result.element,
      }));
  }

  // Convert AI detection results to DetectionResult objects
  private static async convertAIResultsToDetectionResults(
    aiResults: AIDetectionResult[],
  ): Promise<DetectionResult[]> {
    const results: DetectionResult[] = [];

    // Convert API results to DetectionResult objects
    for (const result of aiResults) {
      try {
        // Find the element using the selector from the AI service
        let element: Element | null = null;

        // Try to find the element with the exact selector
        try {
          element = document.querySelector(result.selector);
        } catch (selectorError) {
          console.warn(`Invalid selector from AI: ${result.selector}`);
        }

        // If the selector didn't work, try to clean it up
        if (!element) {
          try {
            // Try a simplified version of the selector
            const simplifiedSelector = result.selector
              .split(" > ")
              .slice(-2)
              .join(" > ");

            element = document.querySelector(simplifiedSelector);
            if (element) {
              console.log(
                `Found element using simplified selector: ${simplifiedSelector}`,
              );
            }
          } catch (simplifyError) {
            console.warn(`Error with simplified selector:`, simplifyError);
          }
        }

        if (element) {
          // Extract value based on element type if not provided by AI
          let extractedValue = result.value;

          if (!extractedValue && this.aiConfig.extractValues) {
            if (element.tagName === "IMG") {
              extractedValue =
                element.getAttribute("src") || element.getAttribute("data-src");
            } else if (element.tagName === "A") {
              extractedValue = element.getAttribute("href");
            } else {
              extractedValue = element.textContent?.trim();
            }
          }

          results.push({
            selector: result.selector,
            confidence: result.confidence,
            value: extractedValue || element.textContent?.trim(),
            element: element,
            aiSuggested: true,
          });
        }
      } catch (selectorError) {
        console.error(
          `Error finding element with selector ${result.selector}:`,
          selectorError,
        );
      }
    }

    return results;
  }

  // Fallback local detection method when DeepSeek API fails
  private static async fallbackLocalDetection(
    field: ScrapingField,
  ): Promise<DetectionResult[]> {
    console.warn("Using fallback local detection for", field.id);

    // Get all visible text elements on the page
    const textElements = Array.from(document.querySelectorAll("*")).filter(
      (el) => {
        const style = window.getComputedStyle(el);
        return (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          el.textContent?.trim().length > 0
        );
      },
    );

    // Analyze elements based on field type
    const results: DetectionResult[] = [];
    let candidateElements: Element[] = [];

    switch (field.id) {
      case "name":
        candidateElements = Array.from(
          document.querySelectorAll('h1, h2, [itemprop="name"]'),
        );
        break;
      case "price":
        candidateElements = Array.from(
          document.querySelectorAll(
            '[itemprop="price"], .price, .product-price',
          ),
        );
        break;
      case "description":
        candidateElements = Array.from(
          document.querySelectorAll(
            '[itemprop="description"], .description, .product-description',
          ),
        );
        break;
      default:
        // For other fields, look for elements with matching class or attribute names
        candidateElements = Array.from(
          document.querySelectorAll(
            `[itemprop="${field.id}"], [class*="${field.id}"], [id*="${field.id}"]`,
          ),
        );
    }

    // Score and rank the candidates
    candidateElements.forEach((element) => {
      // Calculate a confidence score (this would be done by the AI model)
      let confidence = 0.6 + Math.random() * 0.3; // Random score between 0.6 and 0.9

      // Boost confidence for elements with matching attributes
      if (element.getAttribute("itemprop") === field.id) confidence += 0.1;
      if (element.className.toLowerCase().includes(field.id.toLowerCase()))
        confidence += 0.05;
      if (element.id.toLowerCase().includes(field.id.toLowerCase()))
        confidence += 0.05;

      results.push({
        selector: this.generateSelector(element),
        confidence: Math.min(confidence, 0.98), // Cap at 0.98
        value: element.textContent?.trim(),
        element: element,
        aiSuggested: true,
      });
    });

    // Sort by confidence and return top results
    return results.sort((a, b) => b.confidence - a.confidence).slice(0, 3);
  }

  // Configure AI settings
  static configureAI(config: AIServiceConfig): void {
    this.aiConfig = { ...this.aiConfig, ...config };

    // Update the remote AI service with the new configuration
    this.remoteAIService.updateConfig({
      provider: config.provider,
      apiKey: config.apiKey,
      endpoint: config.endpoint,
      modelName: config.modelName,
      confidenceThreshold: config.confidenceThreshold,
      enabled: config.enabled,
      requestTimeout: config.requestTimeout,
      maxRetries: config.maxRetries,
      detectionMode: config.detectionMode,
      includePageContext: config.includePageContext,
      extractValues: config.extractValues,
    });

    console.log(
      `AI service configured: provider=${config.provider}, enabled=${config.enabled}, model=${config.modelName}, threshold=${config.confidenceThreshold}, mode=${config.detectionMode || "balanced"}`,
    );
  }

  private static generateSelector(element: Element): string {
    try {
      const path: string[] = [];
      let current: Element | null = element;

      while (current && current !== document.body) {
        // Try ID first
        if (current.id) {
          path.unshift(`#${current.id}`);
          break;
        }

        // Then try data-testid or other unique attributes
        const testId = current.getAttribute("data-testid");
        if (testId) {
          path.unshift(`[data-testid="${testId}"]`);
          break;
        }

        // Check for itemprop attribute (schema.org)
        const itemprop = current.getAttribute("itemprop");
        if (itemprop) {
          path.unshift(`[itemprop="${itemprop}"]`);
          break;
        }

        // Fall back to tag name and class
        let selector = current.tagName.toLowerCase();
        if (current.className) {
          const classes = Array.from(current.classList)
            .filter((c) => !c.includes("__") && !c.includes("--")) // Filter out BEM modifiers
            .join(".");
          if (classes) selector += `.${classes}`;
        }

        // Add nth-child if needed
        const siblings = current.parentElement?.children || [];
        if (siblings.length > 1) {
          const index = Array.from(siblings).indexOf(current) + 1;
          selector += `:nth-child(${index})`;
        }

        path.unshift(selector);
        current = current.parentElement;
      }

      return path.join(" > ");
    } catch (error) {
      console.error("Error generating selector:", error);
      return "";
    }
  }

  private static getPreviousTextNode(element: Element): string | null {
    try {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null,
      );

      let node = walker.currentNode;
      while (walker.nextNode()) {
        if (walker.currentNode === element) {
          return node.textContent?.trim() || null;
        }
        node = walker.currentNode;
      }

      return null;
    } catch (error) {
      console.error("Error getting previous text node:", error);
      return null;
    }
  }
}
